import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/format.js';
import { Spinner } from './Spinner.jsx';

/**
 * The one button. Every call to action in the store is this component with a
 * variant, which is what keeps focus rings, disabled states, and loading
 * behaviour identical everywhere.
 *
 * A button that is loading stays focusable and keeps its width, so the layout
 * does not jump and a keyboard user does not lose their place.
 */

const base =
  'inline-flex items-center justify-center gap-2 rounded font-medium tracking-[-0.01em] ' +
  'transition-[background-color,border-color,color,opacity] duration-150 ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 ' +
  'disabled:cursor-not-allowed disabled:opacity-45 select-none';

const variants = {
  primary: 'bg-ink text-white hover:bg-ink/88 focus-visible:outline-ink',
  accent: 'bg-accent text-white hover:opacity-88 focus-visible:outline-accent',
  outline: 'border border-mist bg-surface text-ink hover:border-ink hover:bg-paper focus-visible:outline-ink',
  ghost: 'text-ink hover:bg-mist/50 focus-visible:outline-ink',
  quiet: 'text-muted hover:text-ink focus-visible:outline-ink',
  danger: 'bg-danger text-white hover:bg-danger/88 focus-visible:outline-danger',
  link: 'text-ink underline decoration-mist underline-offset-4 hover:decoration-ink focus-visible:outline-ink',
};

const sizes = {
  sm: 'h-9 px-3.5 text-xs',
  md: 'h-11 px-5 text-sm',
  lg: 'h-13 px-7 text-sm',
  icon: 'h-10 w-10 p-0',
};

export const Button = forwardRef(function Button(
  {
    as,
    to,
    href,
    variant = 'primary',
    size = 'md',
    loading = false,
    loadingLabel = 'Working',
    fullWidth = false,
    className,
    children,
    disabled,
    ...props
  },
  ref,
) {
  const classes = cn(base, variants[variant], sizes[size], fullWidth && 'w-full', className);

  const content = (
    <>
      {loading && <Spinner className="size-4" />}
      <span className={cn(loading && 'sr-only')}>{loading ? loadingLabel : children}</span>
      {loading && <span aria-hidden="true">{loadingLabel}</span>}
    </>
  );

  if (to) {
    return (
      <Link ref={ref} to={to} className={classes} {...props}>
        {children}
      </Link>
    );
  }

  if (href) {
    return (
      <a ref={ref} href={href} className={classes} {...props}>
        {children}
      </a>
    );
  }

  const Component = as ?? 'button';

  return (
    <Component
      ref={ref}
      type={Component === 'button' ? (props.type ?? 'button') : undefined}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {content}
    </Component>
  );
});

export default Button;
